'use client';

import React, { useState, useCallback, useRef, useEffect, Component } from 'react';
import dynamic from 'next/dynamic';
import PermissionGate from './PermissionGate';
import GameHUD from './GameHUD';
import CutsceneOverlay from './CutsceneOverlay';
import OutfitUnlockModal from './OutfitUnlockModal';
import CrashRecoveryUI from './CrashRecoveryUI';
import PauseMenu from './PauseMenu';
import GameLogger from './GameLogger';
import SpawnDebugOverlay from './SpawnDebugOverlay';
import PaymentLinksModal from './PaymentLinksModal';
import AreaSelectScreen, { AreaConfig, AREAS } from './AreaSelectScreen';
import LevelCompleteScreen from './LevelCompleteScreen';
import { GameState, HUDData, OutfitData } from '../types/game';
import { OUTFIT_CATALOG, LEVEL_CONFIGS } from './PhaserGame';
import {
  GAME_INTRO_VIDEO,
  TRACK_INTRO_VIDEOS,
  TRACK_COMPLETE_VIDEOS,
  WIPEOUT_VIDEOS,
  pickRandom,
} from '../config/videoConfig';
import { usePostMessageBridge } from '../hooks/usePostMessageBridge';
import {
  useSquishTokenBridge,
} from '../hooks/useSquishTokenBridge';
import { useGameAudio } from '../hooks/useGameAudio';
import { useWordPressConfig } from '../hooks/useWordPressConfig';

const PhaserGame = dynamic(() => import('./PhaserGame'), {
  ssr: false,
  loading: () => (
    <div className="w-screen h-screen bg-[#0d0d0f] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 border-4 border-[#00e5ff] border-t-transparent rounded-full animate-spin" />
        <p className="font-mono-game text-[#00e5ff] text-sm tracking-widest uppercase">Loading Engine</p>
      </div>
    </div>
  ),
});

export type ControlMode = 'tilt' | 'touch' | 'pending';

// ── Error Boundary for Combo Video and Permission Gate
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class GameErrorBoundary extends Component<{ name: string; children: React.ReactNode; onError?: (err: Error) => void }, ErrorBoundaryState> {
  constructor(props: { name: string; children: React.ReactNode; onError?: (err: Error) => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[GameErrorBoundary:${this.props.name}] Caught error:`, error.message, info.componentStack);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      console.warn(`[GameErrorBoundary:${this.props.name}] Rendering fallback — error:`, this.state.error?.message);
      return null; // Silently recover — don't block gameplay
    }
    return this.props.children;
  }
}

// ── Combo video overlay component — small corner overlay, never pauses game
interface ComboVideoOverlayProps {
  videoSrc: string;
  onEnd: () => void;
  mode?: 'combo' | 'wipeout';
}

function ComboVideoOverlay({ videoSrc, onEnd, mode = 'combo' }: ComboVideoOverlayProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [opacity, setOpacity] = React.useState(1);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) { onEnd(); return; }

    video.load();
    const tryPlay = () => {
      video.play().catch(() => { onEnd(); });
    };
    const t = setTimeout(tryPlay, 80);

    // Start fading out after 3.5s
    fadeTimerRef.current = setTimeout(() => {
      setOpacity(0);
      setTimeout(onEnd, 600);
    }, 3500);

    return () => {
      clearTimeout(t);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [videoSrc, onEnd]);

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 55,
        width: 320,
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: mode === 'wipeout' ?'0 0 32px rgba(255,60,60,0.85)' :'0 0 32px rgba(255,214,10,0.85)',
        border: mode === 'wipeout' ?'3px solid rgba(255,60,60,0.9)' :'3px solid rgba(255,214,10,0.9)',
        opacity,
        transition: 'opacity 0.6s ease-out',
        pointerEvents: 'none',
      }}
    >
      {/* Label bar */}
      <div
        style={{
          background: mode === 'wipeout' ?'linear-gradient(90deg, rgba(200,20,20,0.95) 0%, rgba(255,60,60,0.95) 100%)' :'linear-gradient(90deg, rgba(200,120,0,0.95) 0%, rgba(255,200,10,0.95) 100%)',
          padding: '6px 12px',
          fontFamily: 'DM Sans, sans-serif',
          fontWeight: 900,
          fontSize: 15,
          color: '#fff',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          textAlign: 'center',
        }}
      >
        {mode === 'wipeout' ? '💥 WIPEOUT!' : '⚡ EPIC COMBO!'}
      </div>
      <video
        ref={videoRef}
        src={videoSrc}
        style={{ width: '100%', display: 'block', maxHeight: 240, objectFit: 'cover' }}
        playsInline
        muted
        onEnded={() => {
          if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
          setOpacity(0);
          setTimeout(onEnd, 400);
        }}
        onError={() => onEnd()}
      />
    </div>
  );
}

// ── Control Guide Component
interface ControlGuideProps {
  controlMode: ControlMode;
  onDismiss: () => void;
}

function ControlGuide({ controlMode, onDismiss }: ControlGuideProps) {
  const isMobile = controlMode === 'tilt' || controlMode === 'touch';

  const desktopControls = [
    { key: '← →', label: 'Steer Left / Right', icon: '🎮' },
    { key: 'SPACE', label: 'Jump', icon: '🛹' },
    { key: 'B', label: 'Jump', icon: '⬆️' },
    { key: 'N', label: 'Spin', icon: '🌀' },
  ];

  const mobileControls = [
    { key: 'TILT', label: 'Steer Left / Right', icon: '📱' },
    { key: 'JUMP', label: 'Jump button on screen', icon: '🛹' },
    { key: 'SPIN', label: 'Spin button on screen', icon: '🌀' },
  ];

  const controls = isMobile ? mobileControls : desktopControls;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(6px)',
      }}
      onClick={onDismiss}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(13,13,15,0.98) 0%, rgba(20,16,30,0.98) 100%)',
          border: '1.5px solid rgba(0,229,255,0.3)',
          borderRadius: 20,
          padding: '28px 32px',
          maxWidth: 340,
          width: '88%',
          boxShadow: '0 0 50px rgba(0,229,255,0.15)',
          textAlign: 'center',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Title */}
        <div style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 28 }}>🎮</span>
        </div>
        <div
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 900,
            fontSize: '1.1rem',
            color: '#00e5ff',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}
        >
          Controls
        </div>
        <div
          style={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: 11,
            marginBottom: 20,
            letterSpacing: '0.04em',
          }}
        >
          {isMobile ? 'Mobile — Tilt to Steer' : 'Desktop — Keyboard'}
        </div>

        {/* Control rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
          {controls.map((c, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                padding: '8px 14px',
              }}
            >
              <span style={{ fontSize: 18, minWidth: 24 }}>{c.icon}</span>
              <div
                style={{
                  background: 'rgba(0,229,255,0.12)',
                  border: '1px solid rgba(0,229,255,0.3)',
                  borderRadius: 6,
                  padding: '3px 10px',
                  fontFamily: 'monospace',
                  fontWeight: 800,
                  fontSize: 13,
                  color: '#00e5ff',
                  letterSpacing: '0.06em',
                  minWidth: 52,
                  textAlign: 'center',
                }}
              >
                {c.key}
              </div>
              <div
                style={{
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: 13,
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 600,
                  textAlign: 'left',
                  flex: 1,
                }}
              >
                {c.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tip */}
        <div
          style={{
            color: 'rgba(255,255,255,0.35)',
            fontSize: 11,
            marginBottom: 18,
            lineHeight: 1.5,
          }}
        >
          {isMobile
            ? '💡 Tilt your phone to steer. Motion access is required.' :'💡 Hold arrow keys to steer. Press Space or B to jump, N to spin.'}
        </div>

        <button
          onClick={onDismiss}
          style={{
            width: '100%',
            background: 'linear-gradient(135deg, #00e5ff 0%, #0077ff 100%)',
            border: 'none',
            borderRadius: 12,
            padding: '12px 0',
            color: '#0d0d0f',
            fontWeight: 900,
            fontSize: '0.95rem',
            cursor: 'pointer',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          Let&apos;s Ride! 🛹
        </button>
      </div>
    </div>
  );
}

export default function RunSceneShell() {
  const [controlMode, setControlMode] = useState<ControlMode>('pending');
  const [gameState, setGameState] = useState<GameState>('area_select');
  const [logVisible, setLogVisible] = useState(false);
  const [spawnDebugVisible, setSpawnDebugVisible] = useState(false);
  const [showControlGuide, setShowControlGuide] = useState(false);
  const [hudData, setHudData] = useState<HUDData>({
    score: 0,
    comboMultiplier: 1,
    comboCount: 0,
    comboWindowActive: false,
    comboWindowProgress: 0,
    trickName: null,
    trickPoints: 0,
    isAirborne: false,
    airTime: 0,
    speed: 0,
    health: 3,
    distance: 0,
    bestScore: 0,
    levelName: 'Easy Trail',
    runTimeSeconds: 0,
  });
  const [showCutscene, setShowCutscene] = useState(false);
  const [cutsceneVideo, setCutsceneVideo] = useState<string | null>(null);
  const [showOutfitModal, setShowOutfitModal] = useState(false);
  const [selectedOutfit, setSelectedOutfit] = useState<OutfitData | null>(null);
  const [isCrashed, setIsCrashed] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [scoreKey, setScoreKey] = useState(0);
  const [activeOutfitId, setActiveOutfitId] = useState<number>(1);
  const [showOutfitPicker, setShowOutfitPicker] = useState(false);

  // Level progression
  const [currentLevel, setCurrentLevel] = useState(1);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [selectedArea, setSelectedArea] = useState<AreaConfig | null>(null);
  const [completedLevel, setCompletedLevel] = useState(1);
  const [gameKey, setGameKey] = useState(0); // remount PhaserGame on level change

  // Combo video state
  const [comboVideoSrc, setComboVideoSrc] = useState<string | null>(null);
  const [showComboVideo, setShowComboVideo] = useState(false);
  const [comboVideoMode, setComboVideoMode] = useState<'combo' | 'wipeout'>('combo');
  const [showPaymentLinks, setShowPaymentLinks] = useState(false);
  const prevGameStateRef = useRef<GameState>('permission');
  const gameStateRef = useRef<GameState>('permission');
  const controlModeRef = useRef<ControlMode>('pending');

  // Keep controlModeRef in sync
  useEffect(() => { controlModeRef.current = controlMode; }, [controlMode]);

  // ── postMessage bridge — communicate with parent page/plugin
  const { send } = usePostMessageBridge({
    onTokenCheck: (tokens, userId) => {
      console.log('[RunSceneShell] TOKEN_CHECK received — tokens:', tokens, '| userId:', userId);
      // Expose on window so parent plugin can also read it
      if (typeof window !== 'undefined') {
        (window as Window & { surfsterreTokens?: number; surfsterreUserId?: string }).surfsterreTokens = tokens;
        (window as Window & { surfsterreTokens?: number; surfsterreUserId?: string }).surfsterreUserId = userId;
      }
    },
    onOutfitUnlocked: (outfitId) => {
      console.log('[RunSceneShell] OUTFIT_UNLOCKED received — outfitId:', outfitId);
      if (typeof window !== 'undefined') {
        const unlocked: string[] = JSON.parse(localStorage.getItem('surfsterre_unlocked_outfits') || '[]');
        const key = 'outfit-' + outfitId;
        if (!unlocked.includes(key)) {
          unlocked.push(key);
          localStorage.setItem('surfsterre_unlocked_outfits', JSON.stringify(unlocked));
        }
      }
      // Auto-equip the newly unlocked outfit
      setActiveOutfitId(outfitId);
    },
  });

  // ── Squish Games Token Bridge
  const squish = useSquishTokenBridge();

  // ── WordPress Config — outfits and game settings from WP admin
  const { outfits: wpOutfits, loading: wpLoading } = useWordPressConfig();

  // ── Game Audio
  const audio = useGameAudio();
  const [audioMuted, setAudioMuted] = useState(false);

  // Play BGM based on game state
  useEffect(() => {
    if (gameState === 'area_select') {
      audio.playTrackForState('menu');
    } else if (gameState === 'permission') {
      audio.playTrackForState('loading');
    } else if (gameState === 'running') {
      audio.playTrackForState('running', selectedArea?.id);
    }
    // cutscene / paused / crashed — let BGM continue
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, selectedArea?.id]);

  // On game load: fetch wallet balance + skate config
  useEffect(() => {
    console.log('🛹 SQUISH SKATE TOKEN BRIDGE v6.90006 | game component mounted — loading wallet & config');
    squish.fetchWalletBalance().then(bal => {
      if (bal) console.log('🛹 SQUISH SKATE TOKEN BRIDGE v6.90006 | wallet balance loaded →', bal);
    });
    squish.fetchSkateConfig().then(cfg => {
      if (cfg) console.log('🛹 SQUISH SKATE TOKEN BRIDGE v6.90006 | skate config loaded →', cfg);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  const [bestScore] = useState(() => {
    if (typeof window !== 'undefined') {
      return parseInt(localStorage.getItem('surfsterre_best_italy') || '0', 10);
    }
    return 0;
  });

  const handlePermissionGranted = useCallback((mode: ControlMode) => {
    setControlMode(mode);
    setShowControlGuide(true);
    // Fire PLAY_TRIGGERED — user tapped "Play" / granted permission
    send({ type: 'PLAY_TRIGGERED', timestamp: Date.now() });
    // Squish: spend tokens on run start
    squish.spendTokensForRun().then(result => {
      if (result) console.log('🛹 SQUISH SKATE TOKEN BRIDGE v6.90006 | tokens spent for run →', result);
    });
    // Show game intro video if available, otherwise go straight to running
    if (GAME_INTRO_VIDEO) {
      setShowCutscene(true);
      setCutsceneVideo(GAME_INTRO_VIDEO);
      setGameState('cutscene');
    } else {
      setGameState('running');
    }
  }, [send, squish]);

  const handleAreaSelect = useCallback((area: AreaConfig) => {
    setSelectedArea(area);
    // Rainbow Road starts at level 3 (its dedicated level config)
    const startLevel = area.id === 'rainbow_road' ? 3 : 1;
    setCurrentLevel(startLevel);
    setHudData(prev => ({ ...prev, levelName: LEVEL_CONFIGS[startLevel - 1]?.name ?? '' }));
    // Fire GAME_STARTED — player picked an area and the game is beginning
    send({ type: 'GAME_STARTED', area: area.id, level: startLevel });
    // Show track intro video for this area if available
    const trackIntro = TRACK_INTRO_VIDEOS[area.id];
    if (trackIntro) {
      setShowCutscene(true);
      setCutsceneVideo(trackIntro);
      setGameState('cutscene');
      // After track intro, go to permission gate
      setControlMode('pending');
    } else {
      setGameState('permission');
    }
  }, [send]);

  const handleCutsceneEnd = useCallback(() => {
    setShowCutscene(false);
    setCutsceneVideo(null);
    // If we just showed a track intro and controlMode is still pending, go to permission gate
    if (controlModeRef.current === 'pending') {
      setGameState('permission');
    } else {
      setGameState('running');
    }
  }, []);

  const handleHUDUpdate = useCallback((data: Partial<HUDData>) => {
    setHudData(prev => {
      const next = { ...prev, ...data };
      if (data.score !== undefined && data.score !== prev.score) {
        setScoreKey(k => k + 1);
      }
      return next;
    });
  }, []);

  const handleCrash = useCallback(() => {
    setIsCrashed(true);
    setIsShaking(true);
    setGameState('crashed');
    setTimeout(() => setIsShaking(false), 500);
    // Play wipeout sting
    audio.playWipeoutSting();
  }, [audio]);

  const handleRecovery = useCallback(() => {
    setIsCrashed(false);
    setGameState('running');
    window.dispatchEvent(new CustomEvent('surfsterre_restart'));
  }, []);

  const handlePause = useCallback(() => {
    setIsPaused(true);
    setGameState('paused');
  }, []);

  const handleResume = useCallback(() => {
    setIsPaused(false);
    setGameState('running');
  }, []);

  const handleOutfitUnlock = useCallback((outfit: OutfitData) => {
    setSelectedOutfit(outfit);
    setShowOutfitModal(true);
    setIsPaused(true);
    setGameState('paused');
  }, []);

  const handleOutfitModalClose = useCallback(() => {
    setShowOutfitModal(false);
    setSelectedOutfit(null);
    setIsPaused(false);
    setGameState('running');
  }, []);

  const handleOutfitPurchaseSuccess = useCallback((outfitId: string) => {
    if (typeof window !== 'undefined') {
      const unlocked = JSON.parse(localStorage.getItem('surfsterre_unlocked_outfits') || '[]');
      unlocked.push(outfitId);
      localStorage.setItem('surfsterre_unlocked_outfits', JSON.stringify(unlocked));
    }
    setShowOutfitModal(false);
    setSelectedOutfit(null);
    setIsPaused(false);
    setGameState('running');
  }, []);

  // ── Combo video handler — NO game state change, game keeps running
  const handleComboVideo = useCallback((videoSrc: string) => {
    console.log('[RunSceneShell] 🎬 Combo video triggered (overlay only) — src:', videoSrc);
    setComboVideoSrc(videoSrc);
    setComboVideoMode('combo');
    setShowComboVideo(true);
    // Play combo sting
    audio.playComboSting();
    // NOTE: intentionally NOT changing gameState — gameplay continues uninterrupted
  }, [audio]);

  const handleComboVideoEnd = useCallback(() => {
    console.log('[RunSceneShell] Combo video ended');
    setShowComboVideo(false);
    setComboVideoSrc(null);
    window.dispatchEvent(new CustomEvent('surfsterre_combo_video_end'));
  }, []);

  // ── Level complete handler — receives the completed level number
  const handleLevelComplete = useCallback((level: number) => {
    setCompletedLevel(level);
    setGameState('paused');
    setShowLevelComplete(true);
    // Play the track/milestone completion video from config
    const completionVideo = TRACK_COMPLETE_VIDEOS[level];
    if (completionVideo) {
      setShowCutscene(true);
      setCutsceneVideo(completionVideo);
    }
  }, []);

  const handleNextLevel = useCallback(() => {
    setShowLevelComplete(false);
    setShowCutscene(false);
    setCutsceneVideo(null);

    if (completedLevel >= 4) {
      // Show payment links for unlock
      setShowPaymentLinks(true);
      setGameState('paused');
      return;
    }

    const nextLevel = completedLevel + 1;
    setCurrentLevel(nextLevel);
    setIsCrashed(false);

    // If advancing past Rainbow Road (level 3) to level 4, switch area to California
    setSelectedArea(prev => {
      if (prev?.id === 'rainbow_road' && nextLevel === 4) {
        return AREAS.find(a => a.id === 'california') ?? prev;
      }
      return prev;
    });

    setHudData(prev => ({ ...prev, score: 0, comboMultiplier: 1, comboCount: 0, levelName: LEVEL_CONFIGS[nextLevel - 1]?.name ?? '' }));
    setGameKey(k => k + 1); // remount PhaserGame with new level
    setGameState('running');
  }, [completedLevel]);

  const handleReplayLevel = useCallback(() => {
    setShowLevelComplete(false);
    setShowCutscene(false);
    setCutsceneVideo(null);
    setIsCrashed(false);
    setHudData(prev => ({ ...prev, score: 0, comboMultiplier: 1, comboCount: 0 }));
    setGameKey(k => k + 1);
    setGameState('running');
  }, []);

  // ── Fall video handler — plays a random wipeout video as small overlay
  const handleFallVideo = useCallback(() => {
    const url = pickRandom(WIPEOUT_VIDEOS);
    if (!url) return;
    console.log('[RunSceneShell] 💥 Wipeout video triggered (overlay only) — src:', url);
    setComboVideoSrc(url);
    setComboVideoMode('wipeout');
    setShowComboVideo(true);
    // Play wipeout sting
    audio.playWipeoutSting();
    // NOTE: intentionally NOT changing gameState — crash recovery UI shows on top
  }, [audio]);

  // ── Outfit picker — uses WP outfits from WordPress
  const handleSelectOutfit = useCallback((outfitId: number) => {
    // Find outfit from WP list; fall back to OUTFIT_CATALOG for legacy support
    const wpSlot = wpOutfits.find(o => o.id === outfitId);
    const legacySlot = OUTFIT_CATALOG.find(o => o.id === outfitId);

    const outfitName = wpSlot?.name ?? legacySlot?.name ?? `Outfit ${outfitId}`;
    const outfitPrice = wpSlot?.price ?? 299;
    const isPaid = outfitPrice > 0;
    const previewUrl = wpSlot?.previewGif || wpSlot?.spriteSheet || legacySlot?.gifUrl || '';
    const outfitDesc = wpSlot?.description || 'Unlock this outfit to use it in-game.';
    const outfitTags: string[] = wpSlot?.tags ?? ['outfit'];

    if (isPaid) {
      const unlocked: string[] = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('surfsterre_unlocked_outfits') || '[]')
        : [];
      const squishItemId = 'outfit-' + outfitId;

      // Squish: check entitlement ownership
      squish.checkOutfitEntitlement(squishItemId).then(result => {
        console.log('🛹 SQUISH SKATE TOKEN BRIDGE v6.90006 | entitlement check →', squishItemId, result);
        if (result?.owned && !unlocked.includes(squishItemId)) {
          const updated = [...unlocked, squishItemId];
          localStorage.setItem('surfsterre_unlocked_outfits', JSON.stringify(updated));
        }
      });

      if (!unlocked.includes('outfit-' + outfitId) && !(wpSlot?.unlocked)) {
        // Fire OUTFIT_REQUESTED — player tapped a locked paid outfit
        send({ type: 'OUTFIT_REQUESTED', outfitId, outfitName, price: outfitPrice });
        // Squish: purchase entitlement
        squish.purchaseOutfitEntitlement({
          item_id: squishItemId,
          label: outfitName,
          cost: outfitPrice,
        }).then(result => {
          console.log('🛹 SQUISH SKATE TOKEN BRIDGE v6.90006 | outfit purchase →', outfitName, result);
          if (result?.success) {
            const existing: string[] = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('surfsterre_unlocked_outfits') || '[]')
              : [];
            if (!existing.includes(squishItemId)) {
              existing.push(squishItemId);
              localStorage.setItem('surfsterre_unlocked_outfits', JSON.stringify(existing));
            }
          }
        });
        setSelectedOutfit({
          id: 'outfit-' + outfitId,
          name: outfitName,
          description: outfitDesc,
          previewImageUrl: previewUrl,
          price: outfitPrice,
          stripeProductId: '',
          stripePriceId: '',
          tags: outfitTags,
        });
        setShowOutfitModal(true);
        setShowOutfitPicker(false);
        return;
      }
    }

    setActiveOutfitId(outfitId);
    // Fire OUTFIT_CHANGED — player equipped a new outfit
    send({ type: 'OUTFIT_CHANGED', outfitId, outfitName, isPaid });
    window.dispatchEvent(new CustomEvent('surfsterre_outfit_change', { detail: { outfitId } }));
    setShowOutfitPicker(false);
  }, [send, squish, wpOutfits]);

  // ── Check for ?view=paid or ?view=paidall on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view === 'paid' || view === 'paidall') {
      if (view === 'paidall') {
        // Unlock IDs 1–20 to cover both WP outfits and local catalog
        const allIds = Array.from({ length: 20 }, (_, i) => 'outfit-' + (i + 1));
        localStorage.setItem('surfsterre_unlocked_outfits', JSON.stringify(allIds));
      } else {
        // Mark at least outfit 3 as unlocked for $1 purchase
        const existing = JSON.parse(localStorage.getItem('surfsterre_unlocked_outfits') || '[]');
        if (!existing.includes('outfit-3')) existing.push('outfit-3');
        localStorage.setItem('surfsterre_unlocked_outfits', JSON.stringify(existing));
      }
      // Clean URL without reload
      const url = new URL(window.location.href);
      url.searchParams.delete('view');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  return (
    <div
      className={'relative w-screen h-screen overflow-hidden bg-[#0d0d0f] ' + (isShaking ? 'screen-shake' : '')}
    >
      {/* Phaser Canvas — key forces remount on level change */}
      {controlMode !== 'pending' && (
        <PhaserGame
          key={gameKey}
          controlMode={controlMode}
          gameState={gameState}
          onHUDUpdate={handleHUDUpdate}
          onCrash={handleCrash}
          onTrickLanded={() => {}}
          onLevelComplete={handleLevelComplete}
          onOutfitUnlockPrompt={handleOutfitUnlock}
          onComboVideo={handleComboVideo}
          onFallVideo={handleFallVideo}
          activeOutfitId={activeOutfitId}
          currentLevel={currentLevel}
          areaId={selectedArea?.id}
          wpOutfits={wpOutfits}
        />
      )}

      {/* Permission Gate */}
      {gameState === 'permission' && (
        <PermissionGate onGranted={handlePermissionGranted} />
      )}

      {/* Area Select Screen */}
      {gameState === 'area_select' && (
        <AreaSelectScreen onSelect={handleAreaSelect} />
      )}

      {/* Game HUD */}
      {(gameState === 'running' || gameState === 'crashed') && (
        <GameHUD
          hudData={{ ...hudData, bestScore }}
          controlMode={controlMode}
          scoreKey={scoreKey}
          onPause={handlePause}
          onJump={() => window.dispatchEvent(new CustomEvent('surfsterre_jump'))}
          onSpin={() => window.dispatchEvent(new CustomEvent('surfsterre_spin'))}
          onTrick={() => window.dispatchEvent(new CustomEvent('surfsterre_trick'))}
          onCombo={() => window.dispatchEvent(new CustomEvent('surfsterre_combo'))}
          onTiltLeft={() => window.dispatchEvent(new CustomEvent('surfsterre_tilt', { detail: { direction: 'left' } }))}
          onTiltRight={() => window.dispatchEvent(new CustomEvent('surfsterre_tilt', { detail: { direction: 'right' } }))}
        />
      )}

      {/* Level badge — top left during gameplay */}
      {(gameState === 'running' || gameState === 'crashed') && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 40,
            background: 'rgba(0,0,0,0.55)',
            border: '1.5px solid rgba(255,255,255,0.18)',
            borderRadius: 8,
            padding: '4px 10px',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            backdropFilter: 'blur(6px)',
            letterSpacing: '0.04em',
          }}
        >
          {['🥉', '🥈', '🥇', '🏆'][currentLevel - 1]} LVL {currentLevel}
        </div>
      )}

      {/* Outfit Picker Button */}
      {(gameState === 'running' || gameState === 'paused') && (
        <button
          onClick={() => setShowOutfitPicker(v => !v)}
          style={{
            position: 'absolute',
            top: 12,
            right: 64,
            zIndex: 50,
            background: showOutfitPicker ? 'rgba(0,229,255,0.18)' : 'rgba(0,0,0,0.65)',
            border: showOutfitPicker ? '1.5px solid rgba(0,229,255,0.6)' : '1.5px solid rgba(255,255,255,0.22)',
            borderRadius: 10,
            padding: '6px 12px',
            color: showOutfitPicker ? '#00e5ff' : '#fff',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: '0.04em',
            backdropFilter: 'blur(6px)',
            transition: 'background 0.15s, border-color 0.15s, color 0.15s',
          }}
        >
          {wpLoading ? '⏳' : '👗'} {wpOutfits.find(o => o.id === activeOutfitId)?.name ?? `Outfit ${activeOutfitId}`}
        </button>
      )}

      {/* Mute / Unmute BGM Button */}
      {(gameState === 'running' || gameState === 'paused' || gameState === 'crashed') && (
        <button
          onClick={() => {
            const nowMuted = audio.toggleMute();
            setAudioMuted(nowMuted);
          }}
          title={audioMuted ? 'Unmute music' : 'Mute music'}
          style={{
            position: 'absolute',
            top: 12,
            right: 116,
            zIndex: 50,
            background: 'rgba(0,0,0,0.65)',
            border: '1.5px solid rgba(255,255,255,0.22)',
            borderRadius: 10,
            padding: '6px 10px',
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            backdropFilter: 'blur(6px)',
            lineHeight: 1,
          }}
        >
          {audioMuted ? '🔇' : '🔊'}
        </button>
      )}

      {/* Outfit Picker Panel — powered by WordPress */}
      {showOutfitPicker && (
        <div
          style={{
            position: 'absolute',
            top: 50,
            right: 64,
            zIndex: 60,
            background: 'rgba(10,10,14,0.96)',
            border: '1.5px solid rgba(0,229,255,0.2)',
            borderRadius: 16,
            padding: '14px 16px',
            backdropFilter: 'blur(16px)',
            minWidth: 240,
            maxWidth: 300,
            maxHeight: '70vh',
            overflowY: 'auto',
            boxShadow: '0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,229,255,0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ color: '#00e5ff', fontWeight: 800, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              🏄 Select Outfit
            </div>
            {wpLoading && (
              <div style={{ fontSize: 10, color: 'rgba(0,229,255,0.5)', fontStyle: 'italic' }}>Loading…</div>
            )}
          </div>

          {wpOutfits.length === 0 && !wpLoading ? (
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, textAlign: 'center', padding: '8px 0 4px' }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>🔌</div>
              <div style={{ marginBottom: 8 }}>WordPress outfits unavailable — using defaults</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {OUTFIT_CATALOG.map(outfit => {
                  const isActive = outfit.id === activeOutfitId;
                  return (
                    <button
                      key={outfit.id}
                      onClick={() => { setActiveOutfitId(outfit.id); setShowOutfitPicker(false); }}
                      style={{
                        background: isActive ? 'rgba(0,229,255,0.18)' : 'rgba(255,255,255,0.05)',
                        border: isActive ? '2px solid #00e5ff' : '1.5px solid rgba(255,255,255,0.1)',
                        borderRadius: 10,
                        padding: '8px 4px',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: isActive ? 800 : 600,
                        cursor: 'pointer',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 24 }}>🏄</span>
                      <div style={{ fontSize: 10, opacity: 0.9 }}>{outfit.name}</div>
                      <div style={{ fontSize: 9, opacity: 0.5 }}>Free</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {wpOutfits.map(outfit => {
                const isActive = outfit.id === activeOutfitId;
                const unlocked: string[] = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('surfsterre_unlocked_outfits') || '[]')
                  : [];
                const isPaid = outfit.price > 0;
                const isUnlocked = outfit.unlocked || !isPaid || unlocked.includes('outfit-' + outfit.id);
                const accentColor = outfit.accentColor || '#00e5ff';
                const previewSrc = outfit.previewGif || outfit.spriteSheet;

                return (
                  <button
                    key={outfit.id}
                    onClick={() => handleSelectOutfit(outfit.id)}
                    style={{
                      background: isActive
                        ? `linear-gradient(135deg, ${accentColor}33 0%, ${accentColor}18 100%)`
                        : 'rgba(255,255,255,0.05)',
                      border: isActive
                        ? `2px solid ${accentColor}`
                        : '1.5px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      padding: '6px 4px 8px',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: isActive ? 800 : 600,
                      cursor: 'pointer',
                      textAlign: 'center',
                      position: 'relative',
                      opacity: isUnlocked ? 1 : 0.65,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      transition: 'transform 0.15s ease, border-color 0.15s ease',
                      transform: isActive ? 'scale(1.04)' : 'scale(1)',
                      boxShadow: isActive ? `0 0 16px ${accentColor}44` : 'none',
                    }}
                  >
                    <div style={{
                      width: 64,
                      height: 72,
                      borderRadius: 7,
                      overflow: 'hidden',
                      background: `linear-gradient(135deg, ${accentColor}18 0%, rgba(0,0,0,0.4) 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      border: `1px solid ${accentColor}22`,
                    }}>
                      {previewSrc ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={previewSrc}
                          alt={outfit.name + ' outfit preview'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span style={{ fontSize: 28 }}>🏄</span>
                      )}
                      {isPaid && !isUnlocked && (
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0,0,0,0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 22,
                        }}>🔒</div>
                      )}
                      {isActive && (
                        <div style={{
                          position: 'absolute',
                          top: 3,
                          right: 3,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: accentColor,
                          boxShadow: `0 0 6px ${accentColor}`,
                        }} />
                      )}
                    </div>
                    <div style={{ fontSize: 10, opacity: 0.9, lineHeight: 1.2, maxWidth: 68, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {outfit.name}
                    </div>
                    <div style={{ fontSize: 9, opacity: 0.5, color: isPaid && !isUnlocked ? '#ffd700' : '#aaa' }}>
                      {isPaid ? (isUnlocked ? '✓ Owned' : `${outfit.price} 🪙`) : 'Free'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <button
            onClick={() => setShowOutfitPicker(false)}
            style={{
              marginTop: 12,
              width: '100%',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              color: 'rgba(255,255,255,0.5)',
              fontSize: 11,
              padding: '6px 0',
              cursor: 'pointer',
              letterSpacing: '0.04em',
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* Game Logger */}
      {(gameState === 'running' || gameState === 'crashed') && (
        <GameLogger
          visible={logVisible}
          onToggle={() => setLogVisible(v => !v)}
        />
      )}

      {/* Spawn Debug Overlay */}
      {(gameState === 'running' || gameState === 'crashed') && (
        <SpawnDebugOverlay
          visible={spawnDebugVisible}
          onToggle={() => setSpawnDebugVisible(v => !v)}
        />
      )}

      {/* Crash Recovery UI */}
      {gameState === 'crashed' && isCrashed && (
        <CrashRecoveryUI onRecover={handleRecovery} />
      )}

      {/* Pause Menu */}
      {isPaused && !showOutfitModal && !showLevelComplete && (
        <PauseMenu
          onResume={handleResume}
          onRestart={() => {
            setIsPaused(false);
            setGameState('running');
            setHudData(prev => ({ ...prev, score: 0, comboMultiplier: 1, comboCount: 0 }));
            window.dispatchEvent(new CustomEvent('surfsterre_restart'));
          }}
          onQuit={() => { window.location.href = '/'; }}
          currentScore={hudData.score}
          bestScore={bestScore}
        />
      )}

      {/* Combo / Wipeout Video Overlay */}
      {showComboVideo && comboVideoSrc && (
        <GameErrorBoundary
          name="ComboVideoOverlay"
          onError={(err) => {
            console.error('[GameErrorBoundary:ComboVideoOverlay] Error caught, ending overlay:', err.message);
            handleComboVideoEnd();
          }}
        >
          <ComboVideoOverlay videoSrc={comboVideoSrc} onEnd={handleComboVideoEnd} mode={comboVideoMode} />
        </GameErrorBoundary>
      )}

      {/* Cutscene Overlay — plays behind level complete screen or standalone */}
      {showCutscene && cutsceneVideo && !showComboVideo && (
        <CutsceneOverlay
          videoSrc={cutsceneVideo}
          onEnd={handleCutsceneEnd}
          onSkip={handleCutsceneEnd}
        />
      )}

      {/* Level Complete Screen */}
      {showLevelComplete && (
        <LevelCompleteScreen
          completedLevel={completedLevel}
          score={hudData.score}
          onNextLevel={handleNextLevel}
          onReplay={handleReplayLevel}
        />
      )}

      {/* Payment Links Modal */}
      {showPaymentLinks && !showCutscene && !showLevelComplete && (
        <PaymentLinksModal onClose={() => setShowPaymentLinks(false)} />
      )}

      {/* Outfit Unlock Modal */}
      {showOutfitModal && selectedOutfit && (
        <OutfitUnlockModal
          outfit={selectedOutfit}
          onClose={handleOutfitModalClose}
          onPurchaseSuccess={handleOutfitPurchaseSuccess}
        />
      )}

      {/* Control Guide — shown briefly when game starts */}
      {showControlGuide && (
        <ControlGuide
          controlMode={controlMode}
          onDismiss={() => setShowControlGuide(false)}
        />
      )}
    </div>
  );
}